/**
 * Client-side event tracking via Google Tag Manager.
 *
 * The GTM container is loaded first-party through /data/gtm.js (the server-side
 * GTM proxy). We push events onto the dataLayer and let GTM route them to GA4 /
 * Google Ads server-side. There is no direct call to googletagmanager.com or to
 * a custom collection endpoint.
 */

const getDataLayer = () => {
    if (typeof window === 'undefined') return null;
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
};

// prod vs staging signal so GTM can route to the correct GA4 property
const getDevZone = () => {
    if (typeof window === 'undefined') return 'stg';
    const host = window.location.hostname;
    if (host === 'sniffspot.com' || host.endsWith('.sniffspot.com')) {
        return 'prod';
    }
    return 'stg';
};

// GA4 meta parameters the browser cannot derive on its own (cbsa, zip_code, and
// the GA4 user_id), rendered into the page by layouts/_analytics from Ga4Helper.
// GTM's data model does persist the initial push across events, but SPA route
// changes and long sessions make that easy to lose, so every push carries them
// explicitly. Empty object for anonymous visitors — nothing is invented here.
const getUserContext = () => {
    if (typeof window === 'undefined') return {};
    return window.__sniffGa4Context || {};
};

export const trackGtm = ({ action, props = {} }) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) return;

    dataLayer.push({
        event: action,
        dev_zone: getDevZone(),
        ...getUserContext(),
        // last, so a caller that passes its own cbsa/zip_code/user_id wins
        ...props,
    });
};

// Same key as the post-OAuth push in layouts/_analytics.slim.
const registrationSentKey = (userId) => `sniff_ga4_complete_registration.${userId}`;

const registrationAlreadySent = (userId) => {
    try {
        return !!window.localStorage.getItem(registrationSentKey(userId));
    } catch {
        return false;
    }
};

const markRegistrationSent = (userId) => {
    try {
        window.localStorage.setItem(registrationSentKey(userId), '1');
    } catch {
        // storage blocked: the event still goes out, just without the repeat guard
    }
};

export const trackCompleteRegistration = (newUserId) => {
    if (typeof window === 'undefined' || !newUserId) return;

    const userId = String(newUserId);
    if (registrationAlreadySent(userId)) return;

    trackGtm({ action: 'complete_registration', props: { event_category: 'User_front', user_id: userId } });
    markRegistrationSent(userId);
};

export const trackPageView = () => {
    trackGtm({
        action: 'page_view',
        props: {
            page_title: typeof document !== 'undefined' ? document.title : undefined,
            page_location: typeof window !== 'undefined' ? window.location.href : undefined,
        },
    });
};
